import { ShieldCheck, Search, Handshake, RefreshCcw, Truck, FileText, ClipboardCheck } from "lucide-react";
import { useLang } from "../i18n";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";

const SHOWROOM = "https://images.unsplash.com/photo-1585390062628-be8608aa7d83?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const BLOCKS = ["Engine", "Transmission", "Chassis", "Electrics", "Body", "Paperwork"];

export default function About() {
  const { t } = useLang();
  const services = [
    { icon: Search, ...t("about.srv")[0] },
    { icon: Handshake, ...t("about.srv")[1] },
    { icon: RefreshCcw, ...t("about.srv")[2] },
    { icon: Truck, ...t("about.srv")[3] },
    { icon: ShieldCheck, ...t("about.srv")[4] },
    { icon: FileText, ...t("about.srv")[5] },
    { icon: ClipboardCheck, ...t("about.srv")[6] },
  ];

  return (
    <div data-testid="about-page" className="pt-32 pb-24">
      <Seo title={t("seo.aboutTitle")} description={t("seo.aboutDesc")} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-num text-xs tracking-[0.35em] text-[#4499F7] uppercase mb-4">KATACO · EST. 2009</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-50 max-w-3xl">
            {t("about.title")}
          </h1>
          <p className="mt-5 text-lg text-slate-400 max-w-2xl">{t("about.sub")}</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="rounded-3xl overflow-hidden border border-white/10 spotlight-card" data-testid="about-showroom-image">
              <img src={SHOWROOM} alt="KATACO showroom" className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6 text-slate-400 leading-relaxed">
              <p data-testid="about-p1">{t("about.p1")}</p>
              <p data-testid="about-p2">{t("about.p2")}</p>
            </div>
          </Reveal>
        </div>

        <div className="mt-24" data-testid="inspection-section">
          <Reveal>
            <div className="flex items-center gap-4">
              <ShieldCheck size={28} className="text-[#4499F7]" />
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">{t("about.inspTitle")}</h2>
            </div>
            <p className="mt-4 text-slate-400 max-w-2xl">{t("about.inspSub")}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {BLOCKS.map((b, i) => (
              <Reveal key={b} delay={i * 0.06}>
                <div data-testid={`inspection-block-${i}`} className="bg-[#12141D] border border-white/10 rounded-2xl p-6 text-center hover:border-[rgba(68,153,247,0.4)] transition-colors">
                  <p className="font-mono-num text-2xl font-bold text-[#4499F7]">{String(i + 1).padStart(2, "0")}</p>
                  <p className="mt-2 text-sm text-slate-300 font-medium">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-24" data-testid="services-section">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50">{t("about.srvTitle")}</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.t} delay={i * 0.08}>
                  <div data-testid={`service-card-${i}`} className="h-full bg-[#12141D] border border-white/10 rounded-3xl p-8 hover:border-[rgba(68,153,247,0.4)] transition-colors">
                    <Icon size={26} className="text-[#4499F7]" />
                    <h3 className="mt-5 font-display text-xl font-semibold text-slate-50">{s.t}</h3>
                    <p className="mt-3 text-slate-400 leading-relaxed">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
